(ns ui.view.survey.sub
  (:require
   [cljs.reader :as edn]
   [clojure.string :as string]
   [common.date-formats :as date-formats]
   [common.entity :as entity]
   [common.localization :as loc]
   [common.mandatory :as mandatory]
   [common.number-format :as nf]
   [common.schema.data-types :as data-types]
   [common.util :as cu]
   [common.visibility :as visibility]
   [frontend.local-storage :as ls]
   [frontend.state.current-session :as session]
   [potpuri.core :as potpuri]
   [re-frame.core :as rf]
   [ui.view.survey.modals.sub :as modal-sub]))

(rf/reg-sub
 ::num-fmt
 :<-
 [::thousand-separator]
 :<-
 [::decimal-separator]
 (fn [[ts ds] _]
   (-> "#T##0D00"
       (string/replace #"T" ts)
       (string/replace #"D" ds))))

(rf/reg-sub
 ::submitted?
 (fn [db]
   (get db ::submitted?)))

(rf/reg-sub
 ::survey-submitted?
 :<-
 [::submitted?]
 (fn [submitted? [_ survey-id]]
   (get submitted? survey-id)))

(rf/reg-sub
 ::form-style
 (fn [db]
   (get db ::form-style)))

(rf/reg-sub
 ::form-style-survey
 :<-
 [::form-style]
 :<-
 [::configurations]
 (fn [[form-style configurations] [_ survey-id]]
   (cond
     (get-in configurations [survey-id :time-series?])
     :survey-form.style/form

     (get-in configurations [survey-id :multi-survey?])
     (get form-style survey-id :survey-form.style/form)

     :else
     :survey-form.style/form)))

(rf/reg-sub
 ::configurations
 (fn [db]
   (get db ::configurations)))

(rf/reg-sub
 ::access-to-other-surveys?
 (fn [db]
   (get db ::access-to-other-surveys?)))

(rf/reg-sub
 ::thank-you-texts
 (fn [db]
   (get db ::thank-you-texts)))

(rf/reg-sub
 ::questions-ext-ids
 (fn [db]
   (get db ::questions-ext-ids)))

(rf/reg-sub
 ::thank-you-texts-localized
 :<-
 [::selected-lang]
 :<-
 [::thank-you-texts]
 (fn [[lang texts] [_ survey-id]]
   (when (seq texts)
     (or (get-in texts [survey-id lang])
         (get-in texts [survey-id :ENG])
         (some-> (get texts survey-id)
                 (first)
                 (val))))))

(rf/reg-sub
 ::multi-survey?
 :<-
 [::configurations]
 (fn [configurations [_ survey-id]]
   (get-in configurations [survey-id :multi-survey?])))

(rf/reg-sub
 ::show-questions-ext-ids?
 :<-
 [::configurations]
 (fn [configurations [_ survey-id]]
   (get-in configurations [survey-id :show-questions-ext-ids?])))

(rf/reg-sub
 ::target-name
 :<-
 [::configurations]
 (fn [configurations [_ survey-id]]
   (get-in configurations [survey-id :survey-target-name])))

(defn resolve-lang
  [selected-languages survey-id]
  (let [selected-lang (get selected-languages survey-id)]
    (or selected-lang :ENG)))

(defn select-localization
  [selected-lang localized-text]
  (let [selected (get localized-text selected-lang)]
    (if-not (cu/blank-value? selected)
      selected
      (get localized-text :ENG))))

(defn resolve-block-questions-lookup
  [{:keys [block-type] :as block}]
  (when (not= block-type :survey-block/wysiwyg)
    (->> block
         :content
         (keep (fn [{:keys [survey-item-type] :as content-item}]
                 (when (= survey-item-type :survey-item/question)
                   [(:survey-question-id content-item) content-item])))
         (into {}))))

(defn localize-block
  [selected-lang {:keys [block-type _content] :as block}]
  (let [localize (partial select-localization selected-lang)
        block-questions-by-id (resolve-block-questions-lookup block)]
    (-> block
        (assoc :questions-lookup block-questions-by-id)
        (update :label localize)
        (update :content (fn [content]
                           (if (= block-type :survey-block/wysiwyg)
                             (localize content)
                             (->> content
                                  (mapv (fn [{:keys [survey-item-type] :as content-item}]
                                          (case survey-item-type
                                            :survey-item/wysiwyg
                                            (update content-item :content localize)
                                            :survey-item/question
                                            (-> content-item
                                                (assoc :label (localize (:survey-question/label-translations content-item)))
                                                (assoc :info (localize (:survey-question/info-translations content-item)))
                                                (assoc :options (->> content-item
                                                                     :survey-question/options
                                                                     (map (fn [opt]
                                                                            (assoc opt :label (localize (:label opt)))))
                                                                     (sort-by :common/ord))))))))))))))
(rf/reg-sub
 ::survey-config
 :<-
 [::configurations]
 :<-
 [::selected-langs]
 (fn [[configurations selected-languages] [_ survey-id]]
   (let [unlocalizad-config (get configurations survey-id)
         selected-lang (resolve-lang selected-languages survey-id)
         localize (partial select-localization selected-lang)
         localize-block (partial localize-block selected-lang)]
     (-> unlocalizad-config
         (update :label localize)
         (update :blocks (fn [blocks]
                           (->> blocks
                                (mapv localize-block))))))))

(rf/reg-sub
 ::tables-in-config?
 :<-
 [::configurations]
 (fn [configurations [_ survey-id]]
   (let [config (get configurations survey-id)]
     (->> config
          :blocks
          (cu/filter-by :block-type :survey-block/table-block)
          (seq)))))

(rf/reg-sub
 ::uploading?
 (fn [db [_ survey-id]]
   (get-in db [::uploading? survey-id])))

(rf/reg-sub
 ::parsing-error?
 (fn [db [_ survey-id]]
   (get-in db [::parsing-error? survey-id])))

(rf/reg-sub
 ::missing-required-sheets?
 (fn [db [_ survey-id]]
   (get-in db [::missing-required-sheets? survey-id])))

(rf/reg-sub
 ::loading-completed?
 (fn [db [_ _survey-id]]
   (get db ::loading-completed?)))

(rf/reg-sub
 ::survey-loading-complete?
 :<-
 [::loading-completed?]
 (fn [completed [_ survey-id]]
   (get completed survey-id)))

(rf/reg-sub
 ::saving?
 (fn [db]
   (get db ::saving?)))

(rf/reg-sub
 ::saving-survey?
 :<-
 [::saving?]
 (fn [saving? [_ survey-id]]
   (get saving? survey-id)))

(rf/reg-sub
 ::submitting?
 (fn [db]
   (get db ::submitting?)))

(rf/reg-sub
 ::submitting-survey?
 (fn [db [_ survey-id]]
   (get-in db [::submitting? survey-id])))

(rf/reg-sub
 ::error-workbook
 (fn [db [_ survey-id]]
   (get-in db [::error-workbook survey-id])))

(rf/reg-sub
 ::data-import-error
 (fn [db [_ survey-id]]
   (get-in db [::data-import-error survey-id])))

(rf/reg-sub
 ::selected-sheets
 (fn [db [_ survey-id]]
   (get-in db [::selected-sheets survey-id])))

(rf/reg-sub
 ::selecting-sheets?
 (fn [db [_ survey-id]]
   (get-in db [::selecting-sheets? survey-id])))

(rf/reg-sub
 ::resolving-errors?
 (fn [db [_ survey-id]]
   (get-in db [::resolving-errors? survey-id])))

(rf/reg-sub
 ::tesi-custom-excel-import-failed?
 (fn [db [_ survey-id]]
   (get-in db [::tesi-custom-excel-import-failed? survey-id])))

(rf/reg-sub
 ::overwrite-answers-modal-open?
 (fn [db [_ survey-id]]
   (get-in db [::overwrite-answers-modal-open? survey-id])))

(rf/reg-sub
 ::import-data-confirmed?
 (fn [db [_ survey-id]]
   (get-in db [::import-data-confirmed? survey-id])))

(rf/reg-sub
 ::sheet-selection-confirmed?
 (fn [db [_ survey-id]]
   (get-in db [::sheet-selection-confirmed? survey-id])))

(rf/reg-sub
 ::selected-import-file
 (fn [db [_ survey-id]]
   (get-in db [::selected-import-file survey-id])))

(rf/reg-sub
 ::selected-sheets-options
 (fn [db [_ survey-id]]
   (get-in db [::selected-sheets-options survey-id])))

(defn cell-sorter
  [a b]
  (let [a-col (string/replace a #"\d+" "")
        b-col (string/replace b #"\d+" "")]
    (if (not= a-col b-col)
      (compare a-col b-col)
      (let [a-row (string/replace a #"[A-Z]+" "")
            b-row (string/replace b #"[A-Z]+" "")
            a-i (js/parseInt a-row 10)
            b-i (js/parseInt b-row 10)]
        (compare a-i b-i)))))

(rf/reg-sub
 ::cell-parse-errors
 (fn [db [_ survey-id]]
   (->> (get-in db [::cell-parse-errors survey-id])
        (sort (fn [a b]
                (cell-sorter (first a) (first b)))))))

(rf/reg-sub
 ::unconfirmed-parsed-data-by-survey
 (fn [db [_ survey-id]]
   (->> (get-in db [::unconfirmed-parsed-data survey-id]))))

(rf/reg-sub
 ::unconfirmed-parsed-data
 (fn [db [_]]
   (->> (get db ::unconfirmed-parsed-data))))

(defn filter-rows-with-errors
  [data]
  (->> data
       (filter (fn [[_ answers]] (or (some :error answers)
                                     (some :pinned-row? answers))))
       (into {})))

(defn error-column-indexes
  [data]
  (let [rows (->> data vals (filter vector?))
        columns (when (seq rows) (apply map vector rows))]
    (->> columns
         (map-indexed vector)
         (filter (fn [[_ col]] (some :error col)))
         (map first)
         set)))

(defn filter-columns-with-errors
  [data]
  (let [error-cols (error-column-indexes data)]
    (->> data
         (map (fn [[qid answers]]
                [qid (->> answers
                          (map-indexed vector)
                          (filter (fn [[idx _]] (contains? error-cols idx)))
                          (map second)
                          vec)]))
         (into {}))))

(rf/reg-sub
 ::filtered-unconfirmed-parsed-data
 :<- [::unconfirmed-parsed-data]
 :<- [::modal-sub/show-rows-without-errors?]
 :<- [::modal-sub/show-columns-without-errors?]
 (fn [[parsed-data show-rows? show-cols?] [_ survey-id]]
   (let [parsed-data (get parsed-data survey-id)]
     (when (map? parsed-data)
       (cond-> parsed-data
         (not show-rows?) (filter-rows-with-errors)
         (not show-cols?) (filter-columns-with-errors))))))

(defn unresolved-error? [cell]
  (and (:error cell)
       (not= (:error cell) [:resolved [nil]])))

(defn has-unresolved-errors? [parsed-data]
  (some (fn [[_qid answers]]
          (some unresolved-error? answers))
        parsed-data))

(defn resolved-error? [cell]
  (and (:error cell)
       (= (:error cell) [:resolved [nil]])))

(defn has-resolved-errors? [parsed-data]
  (some (fn [[_qid answers]]
          (some resolved-error? answers))
        parsed-data))

(rf/reg-sub
 ::unresolved-errors?
 :<-
 [::unconfirmed-parsed-data]
 (fn [parsed-data [_ survey-id]]
   (let [parsed-data (get parsed-data survey-id)]
     (boolean (has-unresolved-errors? parsed-data)))))

(rf/reg-sub
 ::resolved-errors?
 :<-
 [::unconfirmed-parsed-data]
 (fn [parsed-data [_ survey-id]]
   (let [parsed-data (get parsed-data survey-id)]
     (boolean (has-resolved-errors? parsed-data)))))

(rf/reg-sub
 ::unresolved-error-count
 :<-
 [::unconfirmed-parsed-data]
 (fn [parsed-data [_ survey-id]]
   (let [parsed-data (get parsed-data survey-id)]
     (->> parsed-data
          (vals)
          (mapcat identity)
          (filter unresolved-error?)
          (count)))))

(rf/reg-sub
 ::resolved-error-count
 :<-
 [::unconfirmed-parsed-data]
 (fn [parsed-data [_ survey-id]]
   (let [parsed-data (get parsed-data survey-id)]
     (->> parsed-data
          (vals)
          (mapcat identity)
          (filter resolved-error?)
          (count)))))

(rf/reg-sub
 ::data-has-one-column?
 :<-
 [::unconfirmed-parsed-data]
 (fn [parsed-data [_ survey-id]]
   (let [parsed-data (get parsed-data survey-id)]
     (->> parsed-data
          (vals)
          (first)
          (count)
          (= 1)))))

(rf/reg-sub
 ::editing-error-cell?
 (fn [db [_ survey-id cell-id]]
   (get-in db [::editing-error-cell? survey-id cell-id])))

(rf/reg-sub
 ::selected-langs
 (fn [db _]
   (get db ::selected-langs)))

(rf/reg-sub
 ::validate-counter
 (fn [db [_ survey-id]]
   (get-in db [::validate-counter survey-id] 0)))

(rf/reg-sub
 ::selected-lang
 :<-
 [::selected-langs]
 (fn [selected-languages [_ survey-id]]
   (resolve-lang selected-languages survey-id)))

(rf/reg-sub
 ::supported-languages
 :<-
 [::configurations]
 (fn [configurations [_ survey-id]]
   (let [configuration (get configurations survey-id)]
     (:languages configuration))))

(rf/reg-sub
 ::thousand-separator
 (fn [db _]
   (or (::thousand-separator db)
       (ls/get ::thousand-separator)
       "")))

(rf/reg-sub
 ::thousand-separator-options
 :<-
 [::selected-lang]
 (fn [lang _]
   (->> nf/thousand-separators-options
        (map (fn [opt]
               (assoc opt :label
                      (case (:value opt)
                        "" (loc/tr :thousand-separator/none nil lang)
                        " " (loc/tr :thousand-separator/space nil lang)
                        "," (loc/tr :thousand-separator/comma nil lang)
                        "." (loc/tr :thousand-separator/period nil lang)
                        "'" (loc/tr :thousand-separator/apostrophe nil lang)
                        (loc/tr :separator- (:value opt))))))
        (sort-by :label))))

(rf/reg-sub
 ::decimal-separator
 (fn [db _]
   (or (::decimal-separator db)
       (ls/get ::decimal-separator)
       ".")))

(rf/reg-sub
 ::decimal-separator-options
 :<-
 [::selected-lang]
 :<-
 [::thousand-separator]
 (fn [[lang thousand-separator] _]
   (->> nf/decimal-separators-options
        (cu/remove-by :value thousand-separator)
        (map (fn [opt]
               (assoc opt :label
                      (case (:value opt)
                        "." (loc/tr :decimal-separator/period nil lang)
                        "," (loc/tr :decimal-separator/comma nil lang)
                        (loc/tr :separator- (:value opt))))))
        (sort-by :label))))

(rf/reg-sub
 ::hero-img-url
 :<-
 [::configurations]
 (fn [configurations [_ survey-id]]
   (let [config (get configurations survey-id)]
     (get config :hero-image-url))))

(def debug-atom* (atom {}))

(rf/reg-sub
 ::numeric-questions?
 :<-
 [::configurations]
 (fn [configurations [_ survey-id]]
   (let [config (get configurations survey-id)]
     (reset! debug-atom* config)
     (->> config
          :blocks
          (keep (fn [block]
                  (when (some #{(:block-type block)} [:survey-block/section :survey-block/table-block])
                    (->> block
                         :content
                         (map :survey-question/data-type)
                         (some data-types/numeric-data-types)))))
          (seq)
          (boolean)))))

(rf/reg-sub
 ::show-missing?
 (fn [db]
   (get db ::show-missing?)))

(rf/reg-sub
 ::saved-answer-series
 (fn [db]
   (get db ::saved-answer-series)))

(rf/reg-sub
 ::missing-answers
 (fn [db]
   (get db ::missing-answers)))

(rf/reg-sub
 ::missing-survey-answers
 :<-
 [::missing-answers]
 (fn [missing-answers [_ survey-id]]
   (->> (get missing-answers survey-id)
        (keep (fn [[k missing-answers]]
                (when (seq missing-answers)
                  [k missing-answers])))
        (seq))))

(rf/reg-sub
 ::unsaved-answer-series
 (fn [db]
   (get db ::unsaved-answer-series)))

(rf/reg-sub
 ::previous-answers
 :<-
 [::configurations]
 (fn [configurations [_ survey-id]]
   (get-in configurations [survey-id :previous-answers])))

(rf/reg-sub
 ::question-previous-answer
 :<-
 [::configurations]
 (fn [configurations [_ survey-id question-id]]
   (let [previous-answers (get-in configurations [survey-id :previous-answers])]
     (when (contains? previous-answers question-id)
       {:exists? true
        :value   (get previous-answers question-id)}))))

(rf/reg-sub
 ::unsaved-survey-answers
 :<-
 [::unsaved-answer-series]
 :<-
 [::saved-answer-series]
 (fn [[unsaved-answer-series saved-answer-series] [_ survey-id]]
   (let [survey-unsaved-answer-series (get unsaved-answer-series survey-id)
         survey-saved-answer-series (get saved-answer-series survey-id)
         unsaved-survey-answers (->> survey-unsaved-answer-series
                                     (sort-by first)
                                     (keep (fn [[k unsaved-answers]]
                                             (let [saved-answers (get survey-saved-answer-series k)]
                                               (->> unsaved-answers
                                                    (remove (fn [[k unsaved]]
                                                              (let [saved (get saved-answers k)
                                                                    same? (or (= saved unsaved)
                                                                              (and (cu/blank-value? saved)
                                                                                   (cu/blank-value? unsaved)))]
                                                                same?)))
                                                    (seq)))))
                                     (seq))]
     unsaved-survey-answers)))

(rf/reg-sub
 ::no-answers?
 :<-
 [::unsaved-answer-series]
 :<-
 [::saved-answer-series]
 (fn [[unsaved-answer-series saved-answer-series] [_ survey-id]]
   (and (->> (get unsaved-answer-series survey-id)
             (remove (fn [[_k answers]]
                       (->> answers
                            (remove (fn [[_qid answer]]
                                      (cu/blank-value? answer)))
                            (empty?))))
             (empty?))
        (->> (get saved-answer-series survey-id)
             (remove (fn [[_k answers]]
                       (->> answers
                            (remove (fn [[_qid answer]]
                                      (cu/blank-value? answer)))
                            (empty?))))
             (empty?)))))

(rf/reg-sub
 ::saved-no-answers
 (fn [db]
   (get db ::saved-no-answers)))

(rf/reg-sub
 ::unsaved-no-answers
 (fn [db]
   (get db ::unsaved-no-answers)))

(rf/reg-sub
 ::reference-start-date
 :<-
 [::configurations]
 :<-
 [::selected-langs]
 (fn [[configurations selected-languages] [_ survey-id]]
   (let [lang (get selected-languages survey-id)
         config (get configurations survey-id)
         start-ts (some->> config
                           :reference-period
                           :start-ts)]
     ;; `format-millis` renders nil as the epoch, so an unset period used to
     ;; read "1 Jan 1970" to the respondent. Nothing is better than a date
     ;; that is not the survey's.
     (some-> start-ts
             (date-formats/format-millis (if (= lang :FIN)
                                           "d.M"
                                           "d MMM")
                                         "Etc/UTC")))))

(rf/reg-sub
 ::reference-end-date
 :<-
 [::configurations]
 :<-
 [::selected-langs]
 (fn [[configurations selected-languages] [_ survey-id]]
   (let [lang (get selected-languages survey-id)
         config (get configurations survey-id)
         end-ts (some->> config
                         :reference-period
                         :end-ts)]
     (some-> end-ts
             (date-formats/format-millis (if (= lang :FIN)
                                           "d.M.yyyy"
                                           "d MMM yyyy")
                                         "Etc/UTC")))))

(rf/reg-sub
 ::excel-visible?
 :<-
 [::configurations]
 (fn [configurations [_ survey-id]]
   (let [configuration (get configurations survey-id)
         has-unique-identifier? (->> configuration
                                     :blocks
                                     (cu/filter-by :block-type :survey-block/section)
                                     (keep (fn [block]
                                             (->> block
                                                  :content
                                                  (cu/filter-by :survey-item-type :survey-item/question)
                                                  (filter :survey-question/unique-identifier?)
                                                  (first))))
                                     (seq)
                                     (boolean))]
     (true? (and (get-in configuration [:excel-info-show?])
                 has-unique-identifier?)))))

(rf/reg-sub
 ::loading-excel
 (fn [db [_ survey-id]]
   (get-in db [::loading-excel survey-id])))

(rf/reg-sub
 ::survey-label
 :<-
 [::configurations]
 :<-
 [::selected-langs]
 (fn [[configurations selected-languages] [_ survey-id]]
   (let [selected-lang (resolve-lang selected-languages survey-id)
         label (get-in configurations [survey-id :label selected-lang])]
     label)))

(rf/reg-sub
 ::survey-label-display
 :<-
 [::configurations]
 :<-
 [::selected-langs]
 (fn [[configurations selected-languages] [_ survey-id]]
   (let [configuration (get configurations survey-id)
         selected-lang (resolve-lang selected-languages survey-id)
         ;; Empty rather than the literal string "Label". A template with no
         ;; label in the respondent's language used to head their survey with
         ;; the word Label - untranslated, and meaningless to the person
         ;; reading it. The heading is omitted instead.
         default-label (get-in configuration [:label selected-lang] "")
         target-name (get configuration :survey-target-name)]
     (string/replace (str default-label)
                     "%target%"
                     (str target-name)))))

(rf/reg-sub
 ::target-name-if-not-in-label
 :<-
 [::configurations]
 :<-
 [::selected-langs]
 (fn [[configurations selected-languages] [_ survey-id]]
   (let [configuration (get configurations survey-id)
         selected-lang (resolve-lang selected-languages survey-id)
         default-label (get-in configuration [:label selected-lang] "Label")
         target-name (get configuration :survey-target-name)]
     ;; Only shown separately when the label does not already substitute it.
     (when-not (string/includes? (str default-label) "%target%")
       target-name))))

(defn resolve-value
  [{:keys [no-answer-descriptions-lookup edit-answer-lookup] :as props} {:keys [calculi-type rules] :as rule}]
  (case calculi-type
    :calculi-type/operator-sum
    (let [sum-value (->> rules
                         (map (partial resolve-value props))
                         (remove nil?)
                         (apply +))]
      sum-value)
    :calculi-type/operator-subtraction
    (let [subs-value (->> rules
                          (map (partial resolve-value props))
                          (remove nil?)
                          (apply -))]
      subs-value)
    :calculi-type/operator-division
    (let [div-value (->> rules
                         (map (partial resolve-value props))
                         (remove nil?)
                         (apply /))]
      div-value)
    :calculi-type/operator-multiplication
    (let [mult-value (->> rules
                          (map (partial resolve-value props))
                          (remove nil?)
                          (apply *))]
      mult-value)
    :calculi-type/parenthesis-group
    (let [groupv-val (->> rules
                          (map (partial resolve-value props))
                          (first))]
      groupv-val)
    :calculi-type/constant
    (:value rule)

    :calculi-type/field
    (let [fld (:field rule)
          value (get edit-answer-lookup fld)
          no-answer? (get no-answer-descriptions-lookup fld)
          res (cond
                no-answer?
                nil
                (string? value)
                (let [edn-value (try (edn/read-string value)
                                     (catch js/Object e
                                       (js/console.warn "Could not resolve calculi value : " value " < " e)
                                       nil))]
                  (when (number? edn-value)
                    edn-value))
                (js/isNaN value)
                nil
                (number? value)
                value
                :else
                nil)]
      res)
    (do (js/console.log "Unknown calculi type : " calculi-type
                        "\n" (pr-str rule))
        nil)))

(defn resolve-calculated-value
  [props question]
  (->> question
       :survey-question/calculation-rules
       (map (partial resolve-value props))
       (first)))

(rf/reg-sub
 ::question-value
 :<-
 [::saved-answer-series]
 :<-
 [::unsaved-answer-series]
 (fn [[saved-answer-series unsaved-answer-series] [_ survey-id question-id question-info]]
   (if (= :data.type/calculated-number (:survey-question/data-type question-info))
     (let [saved-at-0 (get-in saved-answer-series [survey-id 0] {})
           unsaved-at-0 (get-in unsaved-answer-series [survey-id 0] {})
           ;; Use empty maps as defaults to prevent deep-merge returning nil
           edit-answer-lookup (potpuri/deep-merge saved-at-0 unsaved-at-0)
           calc-val (resolve-calculated-value {:no-answer-descriptions-lookup nil
                                               :edit-answer-lookup            edit-answer-lookup}
                                              question-info)]
       calc-val)
     (let [v (get-in unsaved-answer-series [survey-id 0 question-id] ::not-set)
           res (if (not= ::not-set v)
                 v
                 (get-in saved-answer-series [survey-id 0 question-id]))]
       res))))

(rf/reg-sub
 ::survey-files
 (fn [db]
   (get db ::survey-files)))

(rf/reg-sub
 ::survey-questions-ext-ids
 (fn [db]
   (get db ::survey-questions-ext-ids)))

(defn resolve-external-id-labels
  [survey-external-ids external-ids]
  (->> external-ids
       (mapv :db/id)
       (mapv (fn [id]
               (get survey-external-ids id)))
       (map (fn [[_battery ext-id]]
              ext-id))
       (string/join ";")))

(rf/reg-sub
 ::survey-question-ext-id-labels
 :<-
 [::survey-questions-ext-ids]
 (fn [all-ext-ids [_ survey-id question]]
   (let [question-external-ids (->> (get question :survey-question/external-ids)
                                    (mapv :db/id)
                                    (mapv (fn [id]
                                            (get-in all-ext-ids [survey-id id]))))]
     (->> question-external-ids
          (map (fn [[battery ext-id]]
                 (let [battery (entity/resolve-entity-name
                                (if (map? battery)
                                  (:db/id battery)
                                  battery)
                                session/get-entity
                                session/reverse-lookup)]
                   (str
                    (when-not (cu/blank-value? battery) (str battery ": ")) ext-id))))
          (string/join ";")))))

(rf/reg-sub
 ::my-file-ids
 (fn [db]
   (get db ::my-file-ids)))
(rf/reg-sub
 ::answer-files
 :<-
 [::survey-files]
 (fn [survey-files [_ survey-id value multi?]]
   (let [process-files (get survey-files survey-id)]
     (if-not multi?
       (get process-files value)
       (select-keys process-files value)))))

(rf/reg-sub
 ::table-values
 :<-
 [::saved-answer-series]
 :<-
 [::unsaved-answer-series]
 (fn [[saved-answer-series unsaved-answer-series] [_ survey-id lang table-block]]
   (let [table-columns (:columns table-block)]
     (->> table-block
          :content
          (cu/filter-by :survey-item-type :survey-item/question)
          (mapv (fn [question-info]
                  (let [{:keys [survey-question-id
                                survey-question/data-type
                                survey-question/label-translations
                                survey-question/data-unit
                                survey-question/info-translations]} question-info
                        merged-answers (potpuri/deep-merge (or saved-answer-series {}) (or unsaved-answer-series {}))
                        res (merge {:id           survey-question-id
                                    :question     (str survey-question-id)
                                    :label        (or (get label-translations lang)
                                                      (get label-translations :ENG))
                                    :data-type    (data-types/number-id-by-data-type data-type)
                                    :data-unit    data-unit
                                    :info         (or (get info-translations lang)
                                                      (get info-translations :ENG))
                                    :column-count (count table-columns)}
                                   (->> (range 0 (count table-columns))
                                        (map (fn [q-idx]
                                               [(keyword (str "resp-" q-idx))
                                                (get-in merged-answers [survey-id q-idx survey-question-id])]))
                                        (into {})))]
                     ;(js/console.log "Table data : " res)
                    res)))))))

(rf/reg-sub
 ::survey-answer-series
 :<-
 [::saved-answer-series]
 :<-
 [::unsaved-answer-series]
 (fn [[saved-answer-series unsaved-answer-series] [_ survey-id]]
   ;; TODO -> Improve with helper to make sure we do not overwrite or nillify
   (let [res (potpuri/deep-merge (get saved-answer-series survey-id {})
                                 (get unsaved-answer-series survey-id {}))]
     (sort-by first res))))

(defn tesi-block-colors [idx]
  (case idx
    0 "bg-[#e3eef3] text-black justify-center"
    1 "bg-[#e7f7d0] text-black justify-center"
    2 "bg-[#eab818] text-black justify-center"
    3 "bg-[#009ab2] text-black justify-center"
    4 "bg-[#f7941d] text-black justify-center"

    "bg-[#F0F0F0] text-black justify-center"))

(rf/reg-sub
 ::question-definitions
 :<-
 [::configurations]
 :<-
 [::selected-langs]
 (fn [[configurations selected-languages] [_]]
   (->> configurations
        (map (fn [[survey-id configuration]]
               (let [pin-to-top-count 0
                     lang (get selected-languages survey-id)
                     question-sections (->> configuration
                                            :blocks
                                            (cu/filter-by :block-type :survey-block/section)
                                            (map-indexed (fn [block-idx block]
                                                           (let [section-label (:label block)
                                                                 section-questions (->> block
                                                                                        :content
                                                                                        (cu/filter-by :survey-item-type :survey-item/question)
                                                                                        (drop (if (zero? block-idx)
                                                                                                pin-to-top-count
                                                                                                0)))]
                                                             (->> section-questions
                                                                  (map-indexed (fn [idx q]
                                                                                 [(:survey-question-id q) {:span  (when (zero? idx)
                                                                                                                    (count section-questions))
                                                                                                           :label (when (zero? idx)
                                                                                                                    section-label)
                                                                                                           :color (case block-idx
                                                                                                                    0 "bg-[#5D4037] text-[#FFFFFF] justify-center"
                                                                                                                    1 "bg-[#2E7D32] text-[#FFFFFF] justify-center"
                                                                                                                    2 "bg-[#B39DDB] text-[#1A1A1A] justify-center"
                                                                                                                    3 "bg-[#1A237E] text-[#FFFFFF] justify-center"
                                                                                                                    4 "bg-[#B0BEC5] text-[#1A1A1A] justify-center"

                                                                                                                    "bg-complement-light text-complement-light-text justify-center")}]))))))
                                            (mapcat identity)
                                            (into {}))
                     questions (->> configuration
                                    :blocks
                                    (map-indexed (fn [block-idx {:keys [block-type content] :as _block}]
                                                   (when (= block-type :survey-block/section)
                                                     (->> content
                                                          (cu/filter-by :survey-item-type :survey-item/question)
                                                          (drop (if (zero? block-idx)
                                                                  pin-to-top-count
                                                                  0))))))
                                    (mapcat identity)
                                    (mapv (fn [question]
                                            (assoc question
                                                   :localized-options (->> question
                                                                           :survey-question/options
                                                                           (map (fn [{:keys [label] :as opt}]
                                                                                  (cond (string? label)
                                                                                        opt
                                                                                        (map? label)
                                                                                        (assoc opt :label (or (get label lang)
                                                                                                              (get label :ENG)
                                                                                                              (val (first label))))
                                                                                        :else
                                                                                        (do (js/console.log "Could not resolve survey-question option label localization " lang " << " opt)
                                                                                            nil))))
                                                                           (sort-by :common/ord)
                                                                           (vec))
                                                   :option-value-lookup (if (= :data.type/boolean (:survey-question/data-type question))
                                                                          (->> [["true" true]
                                                                                ["kyllä" true]
                                                                                ["yes" true]
                                                                                ["1" true]
                                                                                ["0" false]
                                                                                ["no" false]
                                                                                ["ei" false]
                                                                                ["false" false]
                                                                                [true true]
                                                                                [false false]]
                                                                               (into {}))
                                                                          (->> question
                                                                               :survey-question/options
                                                                               (mapcat (fn [{:keys [value label] :as opt}]
                                                                                         (concat [[(cu/str-trim-lcase value) value]]
                                                                                                 (cond (string? label)
                                                                                                       [(cu/str-trim-lcase label) value]
                                                                                                       (map? label)
                                                                                                       (->> label
                                                                                                            (map (fn [[_lang label]]
                                                                                                                   [(cu/str-trim-lcase label) value]))
                                                                                                            (doall))
                                                                                                       :else
                                                                                                       (do (js/console.log "Could not resolve survey-question label lookup from " opt)
                                                                                                           nil)))))
                                                                               (into {})))))))]
                 [survey-id [questions question-sections]])))
        (into {}))))

(rf/reg-sub
 ::survey-questions
 :<-
 [::question-definitions]
 (fn [question-defs [_ survey-id]]
   (get question-defs survey-id)))

(rf/reg-sub
 ::series-count
 :<-
 [::saved-answer-series]
 :<-
 [::unsaved-answer-series]
 (fn [[saved-answer-series unsaved-answer-series] [_ survey-id]]
   (let [saved-data (get saved-answer-series survey-id)
         unsaved-data (get unsaved-answer-series survey-id)
         data (merge saved-data unsaved-data)]
     (count data))))

(defn dom-id
  [survey-id series-index survey-question-id]
  (str "survey-question-" survey-id "-" series-index "-" survey-question-id))

(rf/reg-sub
 ::grid-data
 :<-
 [::saved-answer-series]
 :<-
 [::unsaved-answer-series]
 :<-
 [::question-definitions]
 :<-
 [::show-missing?]
 :<-
 [::selected-langs]
 (fn [[saved-answer-series unsaved-answer-series question-definitions show-missing? selected-languages] [_ survey-id]]
   (let [saved-data (get saved-answer-series survey-id {})
         lang (get selected-languages survey-id)
         unsaved-data (get unsaved-answer-series survey-id {})
         [questions question-sections] (get question-definitions survey-id)
         show-missing-series-data? (get show-missing? survey-id)
         data (potpuri/deep-merge saved-data unsaved-data)
         grid-data (->> questions
                        (map-indexed
                         (fn [idx question]
                             ;_(js/console.log "GRID Question : " question)
                           (let [question-id (:survey-question-id question)
                                 series-data (->> (range 0 (count data))
                                                  (mapv (fn [series-index]
                                                          (let [missing-series-questions (get-in show-missing-series-data? [series-index])]
                                                            {:value           (get-in data [series-index question-id])
                                                             :field-id        (str "resp-" series-index)
                                                             :dom-id          (dom-id survey-id series-index question-id)
                                                             :survey-id       survey-id
                                                             :series-index    series-index
                                                             :question-id     question-id
                                                             :show-mandatory? (some #{question-id} missing-series-questions)
                                                             :lang            lang
                                                             :visible?        (visibility/calc-visibility (merge (get-in saved-answer-series [survey-id series-index])
                                                                                                                 (get-in unsaved-answer-series [survey-id series-index]))
                                                                                                          (:visibility-rules question)
                                                                                                          (:visibility question))}))))]
                             (merge {:question-index      idx
                                     :question-id         question-id
                                     :id                  question-id
                                     :lang                lang
                                     :section             (let [labels (get-in question-sections [question-id :label])]
                                                            (or (get labels lang)
                                                                (get labels :ENG)))
                                     :label               (get-in question [:survey-question/label-translations lang])
                                     :data-unit           (:survey-question/data-unit question)
                                     :data-type           (pr-str (:survey-question/data-type question))
                                     :option-value-lookup (:option-value-lookup question)
                                     :series-data         series-data}
                                    (->> series-data
                                         (map (fn [series-data]
                                                [(:field-id series-data) (:value series-data)]))
                                         (into {}))))))
                        (vec))]
     grid-data)))

(rf/reg-sub
 ::missing-modal-questions
 :<-
 [::question-definitions]
 :<-
 [::show-missing?]
 :<-
 [::selected-langs]
 (fn [[question-definitions show-missing? selected-languages] [_ survey-id]]
   (let [lang (get selected-languages survey-id)
         [questions _] (get question-definitions survey-id)
         show-missing-series-data? (get show-missing? survey-id)
         all-missing-questions-ids (->> show-missing-series-data?
                                        (mapcat (fn [[_ question-ids]]
                                                  question-ids))
                                        (set))
         missing-question-labels (->> questions
                                      (keep (fn [question]
                                              (let [question-id (:survey-question-id question)]
                                                (when (some #{question-id} all-missing-questions-ids)
                                                  (get-in question [:survey-question/label-translations lang]))))))]
     missing-question-labels)))

(rf/reg-sub
 ::question-no-answer?
 :<-
 [::saved-no-answers]
 :<-
 [::unsaved-no-answers]
 (fn [[saved-no-answers unsaved-no-answers] [_ survey-id question-id]]
   (or (get-in unsaved-no-answers [survey-id question-id])
       (get-in saved-no-answers [survey-id question-id]))))

(rf/reg-sub
 ::question-visible?
 :<-
 [::saved-answer-series]
 :<-
 [::unsaved-answer-series]
 (fn [[saved-answer-series unsaved-answer-series] [_ survey-id series-index question-config]]
   (case (:visibility question-config)
     nil true
     :visibility/always true
     :visibility/hidden false
     :visibility/conditional
     (let [answer-lookup (potpuri/deep-merge (get-in saved-answer-series [survey-id series-index] {})
                                             (get-in unsaved-answer-series [survey-id series-index] {}))
           rules (:visibility-rules question-config)
           visible? (visibility/calc-visibility answer-lookup
                                                rules
                                                (:visibility question-config))]
       visible?)
     (do (js/console.error "Unknown visibility : " question-config)
         false))))

(rf/reg-sub
 ::question-mandatory?
 :<-
 [::saved-answer-series]
 :<-
 [::unsaved-answer-series]
 (fn [[saved-answer-series unsaved-answer-series] [_ survey-id series-index question-config]]
   (let [calc-mandatoriness (fn []
                              (let [answer-lookup (potpuri/deep-merge (get-in saved-answer-series [survey-id series-index] {})
                                                                      (get-in unsaved-answer-series [survey-id series-index] {}))
                                    rules (:mandatoriness-rules question-config)
                                    visible? (mandatory/calc-mandatoriness true answer-lookup
                                                                           rules
                                                                           (:mandatoriness question-config))]
                                visible?))]
     (case (:mandatoriness question-config)
       :mandatory/optional false
       :mandatory/always true
       :mandatory/when-visible true
       :mandatory/conditional (calc-mandatoriness)
       :mandatory/conditional-when-visible (calc-mandatoriness)
       true))))

(rf/reg-sub
 ::survey-visibility
 :<-
 [::saved-answer-series]
 :<-
 [::unsaved-answer-series]
 :<-
 [::question-definitions]
 (fn [[saved-answer-series unsaved-answer-series question-definitions] [_ survey-id]]
   (let [[questions _] (get question-definitions survey-id)
         current-data (potpuri/deep-merge (get saved-answer-series survey-id {})
                                          (get unsaved-answer-series survey-id {}))]
     (->> current-data
          (map (fn [[series-index series-data]]
                 [series-index (->> questions
                                    (map (fn [question-config]
                                           [(:survey-question-id question-config)
                                            (case (:visibility question-config)
                                              :visibility/always true
                                              :visibility/hidden false
                                              :visibility/conditional
                                              (visibility/calc-visibility series-data
                                                                          (:visibility-rules question-config)
                                                                          (:visibility question-config))
                                              (do (js/console.error "Unknown visibility : " question-config)
                                                  false))]))
                                    (sort-by first))]))
          (sort-by first)))))

(rf/reg-sub
 ::show-missing-question?
 :<-
 [::show-missing?]
 (fn [show-missing? [_ survey-id series-index question-id]]
   (let [show-missing-series (get-in show-missing? [survey-id])
         show-missing-questions (get show-missing-series series-index)
         show? (some #{question-id} show-missing-questions)]
     show?)))

(rf/reg-sub
 ::survey-processes-last-updated-on
 (fn [db]
   (get db ::survey-processes-last-updated-on)))
